import { useState, useRef, useEffect } from 'react';
import { useData } from './DataContext.jsx';
import { fmtMin, durationMin } from './lib/format.js';
import { Modal } from './components.jsx';
import { IconX } from './icons.jsx';

// Shown after the user stops the timer. Gives them one place to confirm/adjust
// the time range, pick a project, and add a description before the entry
// lands in the database. Cancel = discard (nothing saved).
function StopTimerModal({ fromTime, toTime, projectId: initialProjectId, onSave, onDiscard }) {
  const { projects, addTimeEntry } = useData();
  const [from, setFrom] = useState(fromTime);
  const [to, setTo] = useState(toTime);
  const [projectId, setProjectId] = useState(initialProjectId || projects[0]?.id || '');
  const [desc, setDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const descRef = useRef(null);

  useEffect(() => { descRef.current?.focus(); }, []);

  const dur = durationMin(from, to);
  const canSave = !!projectId && dur > 0;

  async function save() {
    if (!canSave || saving) return;
    setSaving(true); setErr(null);
    try {
      await addTimeEntry({ project_id: projectId, from_time: from, to_time: to, description: desc });
      onSave(dur);
    } catch (e) {
      setErr(e.message || 'Fehler beim Speichern');
      setSaving(false);
    }
  }

  return (
    <Modal onClose={onDiscard}>
      <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--stroke)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div className="eyebrow">Zeiteintrag speichern</div>
          <h2 className="serif" style={{ fontSize: 22, fontWeight: 500, margin: '4px 0 0', letterSpacing: '-0.01em' }}>
            {dur > 0 ? fmtMin(dur) : '—'}
          </h2>
        </div>
        <button className="icon-btn" onClick={onDiscard}><IconX size={14}/></button>
      </div>

      <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="field">
          <label>Projekt</label>
          {projects.length === 0 ? (
            <div className="tiny" style={{ color: 'var(--warn)', padding: 8, background: 'var(--warn-soft)', borderRadius: 6 }}>
              Keine Projekte vorhanden. Lege erst ein Projekt an.
            </div>
          ) : (
            <select className="select" value={projectId} onChange={e => setProjectId(e.target.value)}>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          )}
        </div>

        <div className="field">
          <label>Beschreibung</label>
          <input
            ref={descRef}
            className="input"
            value={desc}
            onChange={e => setDesc(e.target.value)}
            placeholder="Was hast du gemacht?"
            onKeyDown={e => { if (e.key === 'Enter' && canSave) save(); }}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div className="field">
            <label>Von</label>
            <input type="time" className="input" value={from} onChange={e => setFrom(e.target.value)}/>
          </div>
          <div className="field">
            <label>Bis</label>
            <input type="time" className="input" value={to} onChange={e => setTo(e.target.value)}/>
          </div>
        </div>

        {err && <div className="tiny" style={{ color: 'var(--crit)' }}>{err}</div>}
      </div>

      <div style={{ padding: '14px 24px', borderTop: '1px solid var(--stroke)', display: 'flex', justifyContent: 'space-between', gap: 8 }}>
        <button className="btn btn-ghost" onClick={onDiscard} disabled={saving}
                style={{ color: 'var(--ink-3)' }}>
          Verwerfen
        </button>
        <button className="btn btn-primary" onClick={save} disabled={!canSave || saving}
                style={{ opacity: (canSave && !saving) ? 1 : 0.45 }}>
          {saving ? 'Speichere…' : 'Eintragen'}
        </button>
      </div>
    </Modal>
  );
}

export { StopTimerModal };
